<template>
    <v-dialog
        v-model="visible"
        :show="true"
        persistent
        :max-width="500"
        :fullscreen="$vuetify.breakpoint.xs"
        :transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : 'dialog-transition'"
        overlay-color="#2c2c38"
        :overlay-opacity="0.8"
    >
        <v-card v-if="!paid" class="subscription">
            <v-card-title class="text-h6 justify-center">{{ $t('subscription.heading') }}</v-card-title>
            <v-card-text>
                <v-alert
                    class="subscription-alert"
                    prominent
                    color="#59b200"
                    type="info"
                    text
                >
                    <span v-if="subscriptionStatus === 'trial_expired'">{{ $t('subscription.trial_expired') }}</span>
                    <span v-else-if="subscriptionStatus === 'expired_contact_support'">{{ $t('subscription.expired_contact_support') }}</span>
                    <span v-else-if="subscriptionStatus === 'canceled'">{{ $t('subscription.canceled') }}</span>
                    <span v-else-if="subscriptionStatus === 'payment_failed'">{{ $t('subscription.payment_failed') }}</span>
                    <span v-else-if="subscriptionStatus === 'paused'">{{ $t('subscription.paused') }}{{ nextBillDate }}</span>
                </v-alert>

                <div v-if="subscriptionStatus !== 'expired_contact_support'" class="flex justify-center mt-6 mb-1">

                    <template v-if="subscriptionStatus === 'trial_expired' || subscriptionStatus === 'canceled'">
                        <v-btn
                            class="white--text mr-3"
                            color="#59b200"
                            depressed
                            large
                            @click="checkout()"
                        >
                            <v-icon class="mr-1" size="10">fas fa-chevron-double-right</v-icon>
                            {{ $t('general.continue') }}
                        </v-btn>

                        <v-btn
                            class="white--text"
                            color="#59b200"
                            depressed
                            large
                            @click="logoutTenant()"
                        >
                            <v-icon class="mr-1" size="10">fal fa-sign-out</v-icon>
                            {{ $t('general.log_out') }}
                        </v-btn>
                    </template>

                    <template v-if="subscriptionStatus === 'payment_failed'">
                        <v-btn
                            class="mr-3"
                            outlined
                            depressed
                            large
                            @click="updatePaymentDetails()"
                        >
                            {{ $t('subscription.update_payment_details') }}
                        </v-btn>

                        <v-btn
                            class="white--text w150"
                            color="#59b200"
                            depressed
                            large
                            @click="restartSubscription()"
                        >
                            {{ $t('subscription.pay') }}
                        </v-btn>
                    </template>

                    <v-btn
                        v-if="subscriptionStatus === 'paused'"
                        class="white--text"
                        color="#59b200"
                        block
                        depressed
                        large
                        @click="restartSubscription()"
                    >
                        {{ $t('subscription.activate') }}
                        <v-icon class="ml-1" size="10">fas fa-chevron-double-right</v-icon>
                    </v-btn>

                </div>
            </v-card-text>
        </v-card>

        <v-card v-else class="subscription">
            <v-card-title class="text-h6">{{ $t('general.payment_successful') }}</v-card-title>
            <v-card-text>
                <v-alert
                    class="subscription-alert"
                    prominent
                    color="#59b200"
                    type="success"
                    text
                >
                    {{ $t('subscription.thank_you_continue') }}
                </v-alert>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import ApiService from '../services/ApiService';
import TenantService from '../services/TenantService';

export default {
    created() {
        if(process.env.VUE_APP_ENABLE_BILLING !== 'true') {
            return
        }

        this.eventBus.$on('notification-received', this.onNotificationReceived);
        this.eventBus.$on('subscription-expired', this.onSubscriptionExpired);
    },

    beforeDestroy() {
        this.eventBus.$off('notification-received', this.onNotificationReceived);
        this.eventBus.$off('subscription-expired', this.onSubscriptionExpired);
    },

    data() {
        return {
            visible: false,
            paid: false
        };
    },

    computed: {
        subscriptionStatus() {
            if(!this.tenant.subscription) {
                return !this.tenant.venue.settings.subscription_payments_enabled
                    ? 'expired_contact_support'
                    : 'trial_expired';
            } else if(this.tenant.subscription.p_status === 'deleted') {
                return 'canceled';
            } else if(this.tenant.subscription.p_paused_reason === 'delinquent') {
                return 'payment_failed';
            } else if(this.tenant.subscription.p_paused_reason === 'voluntary') {
                return 'paused';
            } else {
                return 'active';
            }
        },

        nextBillDate() {
            let date = moment(this.tenant.subscription.p_next_bill_date);

            if(date.isBefore(moment().startOf('day').add(1, 'days'))) {
                return '.';
            }

            let dateString = ['me', 'sr', 'ba'].includes(this.tenant.venue.locale)
                ? date.format('DD.MM.Y')
                : date.format('Y-MM-DD');

            return ' ' + this.$t('subscription.on_date', { date: dateString })
        }
    },

    methods: {
        async onNotificationReceived(notification) {
            if (notification.type === 'subscription-created' || notification.type === 'subscription-updated') {
                setTimeout(() => {
                    this.hide();
                }, 4000);
            }

            if (notification.type === 'subscription-canceled' || notification.type === 'subscription-paused') {
                await this.reloadTenant();
                this.visible = true;
                this.paid = false;
            }
        },

        onSubscriptionExpired() {
            this.visible = true
        },

        checkout() {
            if(!Paddle) {
                return
            }

            this.visible = false;

            Paddle.Checkout.open({
                product: process.env.VUE_APP_PADDLE_PRODUCT_ID,
                title: this.$t('subscription.plan_title'),
                message: this.$t('subscription.plan_message'),
                email: this.tenant.email,
                country: this.tenant.venue.country,
                passthrough: JSON.stringify({ tenant_id: this.tenant.id }),
                successCallback: this.successfullPurchase,
                closeCallback: this.failedPurchase
            });
        },

        updatePaymentDetails() {
            if(!Paddle) {
                return
            }

            this.visible = false;

            Paddle.Checkout.open({
                override: this.tenant.subscription.p_update_url,
                passthrough: JSON.stringify({ tenant_id: this.tenant.id }),
                successCallback: () => {
                    setTimeout(() => {
                        this.visible = true
                    }, 1000)
                },
                closeCallback: this.failedPurchase
            });
        },

        restartSubscription() {
            ApiService.post('/subscription/update', { pause: false })
                .then(response => {
                    this.successfullPurchase();
                }).catch(error => {
                    console.log(error)
                });
        },

        successfullPurchase() {
            this.paid = true;
            this.visible = true;

            setTimeout(() => {
                this.hide();
                location.reload();
            }, 4000);
        },

        failedPurchase() {
            this.paid = false;
            this.visible = true;
        },

        hide() {
            this.visible = false;
            setTimeout(() => {
                this.paid = false;
            }, 500);
        },

        async reloadTenant(){
            const response = await ApiService.get('/me');
            TenantService.setTenant(response.data);
        },

        /**
         * Log out Tenant
         */
        logoutTenant() {
            TenantService.logout();
            this.$router.push({ name: 'tenantLogin' }).catch(err => {});
        },
    }
};
</script>

<style lang="scss" scoped>
.subscription {
    display: flex;
    flex-direction: column;

    .title {
        justify-content: center;
        font-weight: 400;
        font-size: 2rem;
        padding: 20px 0;
    }

    .subscription-alert {
        color: black !important;
        border: 1px solid #e8e8e8;

        &:before {
            background: #cccccc !important;
        }
    }

    .finish {
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;

        .success-icon {
            height: 100px;
            width: 100px;
            border: 5px solid #4baf4f;
            border-radius: 50%;
            color: #4baf4f;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .actions {
        padding: 0 24px 24px;
    }
}
</style>
